(() => {
  'use strict';

  /* ============================================================
     SELECTORS
     ============================================================ */

  const ROOT_SELECTOR = '[data-fs-quick-filters]';
  const FORMAT_SELECTOR = '[data-fs-format]';
  const GENDER_SELECTOR = '[data-fs-gender]';

  const UNISEX_ROW_SELECTOR = '[data-fs-unisex-row]';
  const UNISEX_TOGGLE_SELECTOR = '[data-fs-unisex-toggle]';

  const SCENT_SELECTOR = '[data-fs-scent]';
  const GENDER_VALUE_SELECTOR = '[data-fs-gender-value]';
  const SCENT_VALUE_SELECTOR = '[data-fs-scent-value]';

  const BRAND_TOGGLE_SELECTOR = '[data-fs-brand-toggle]';
  const SORT_TOGGLE_SELECTOR = '[data-fs-sort-toggle]';

  const BRAND_PANEL_SELECTOR = '[data-fs-brand-panel]';
  const SORT_PANEL_SELECTOR = '[data-fs-sort-panel]';

  const BRAND_OPTION_SELECTOR = '[data-fs-brand-value]';
  const BRAND_COLLECTION_OPTION_SELECTOR = '[data-fs-brand-collection-url]';
  const SORT_OPTION_SELECTOR = '[data-fs-sort-value]';


  /* ============================================================
     SCENTS
     ============================================================ */

  const SCENT_KEYS = [
    'fresh',
    'citrus',
    'fruity',
    'floral',
    'green',
    'sweet',
    'milky',
    'powdery',
    'woody',
    'spicy',
    'smoky',
    'leather',
    'dark',
    'vintage'
  ];

  const SCENT_LABELS = {
    fresh: 'Fresh',
    citrus: 'Citrus',
    fruity: 'Fruity',
    floral: 'Floral',
    green: 'Green',
    sweet: 'Sweet',
    milky: 'Milky',
    powdery: 'Powdery',
    woody: 'Woody',
    spicy: 'Spicy',
    smoky: 'Smoky',
    leather: 'Leather',
    dark: 'Dark',
    vintage: 'Vintage'
  };


  /* ============================================================
     INITIALIZATION GUARD
     ============================================================ */

  if (window.fsQuickFiltersInitialized) {
    document.dispatchEvent(
      new CustomEvent('fs-quick-filters:sync')
    );
    return;
  }

  window.fsQuickFiltersInitialized = true;

  let activeRequest = null;
  let syncTimer = null;
  let openPanelState = null;


  /* ============================================================
     GENERAL HELPERS
     ============================================================ */

  function normalizeKey(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }


  function getRoots() {
    return Array.from(
      document.querySelectorAll(ROOT_SELECTOR)
    );
  }


  function getRoot(element) {
    if (!element) {
      return null;
    }

    const normalRoot = element.closest(ROOT_SELECTOR);

    if (normalRoot) {
      return normalRoot;
    }

    if (
      openPanelState &&
      openPanelState.panel &&
      openPanelState.panel.contains(element)
    ) {
      return openPanelState.root;
    }

    return null;
  }


  function unique(values) {
    return Array.from(
      new Set(
        values.filter(Boolean)
      )
    );
  }


  function clearPaging(url) {
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
  }


  /* ============================================================
     PRODUCT TYPE / FORMAT HELPERS
     ============================================================ */

  function isDedicatedFormatCollection(root) {
    const pathname =
      window.location.pathname
        .toLowerCase()
        .replace(/\/+$/, '');

    return (
      pathname === '/collections/fairscent-loop' ||
      pathname === '/collections/fairscent-pulse'
    );
  }


  function isBrandCollection(root) {
    return Boolean(
      root &&
      root.dataset.isBrandCollection === 'true'
    );
  }


  function removeProductTypeFilters(url) {
    const keys = Array.from(
      url.searchParams.keys()
    );

    keys.forEach(key => {
      const normalized = String(key).toLowerCase();

      if (
        normalized.includes('product_type') ||
        normalized.includes('product-type')
      ) {
        url.searchParams.delete(key);
      }
    });
  }


  function getFormatFilterValue(root, formatKey) {
    if (!root) {
      return '';
    }

    return formatKey === 'pulse'
      ? (root.dataset.pulseTypeValue || '')
      : (root.dataset.loopTypeValue || '');
  }


  function hasActiveFormatFilter(root, url) {
    if (!root) {
      return false;
    }

    const paramName =
      root.dataset.typeParam;

    if (!paramName) {
      return false;
    }

    const selected = new Set(
      getFilterValues(url, paramName)
    );

    const loopValue =
      root.dataset.loopTypeValue || '';

    const pulseValue =
      root.dataset.pulseTypeValue || '';

    return Boolean(
      (loopValue && selected.has(loopValue)) ||
      (pulseValue && selected.has(pulseValue))
    );
  }


  /* ============================================================
     URL FILTER VALUES
     ============================================================ */

  function getFilterValues(url, paramName) {
    if (!paramName) {
      return [];
    }

    return unique(
      url.searchParams
        .getAll(paramName)
        .flatMap(value =>
          String(value).split(',')
        )
        .map(value => value.trim())
        .filter(Boolean)
    );
  }


  function cleanHistoryUrl(url) {
    const clean = new URL(url.toString());

    clean.searchParams.delete('section_id');

    return (
      clean.pathname +
      clean.search +
      clean.hash
    );
  }


  /* ============================================================
     FILTER VALUE MAPS
     ============================================================ */

  function makeMap(root, selector) {
    const map = new Map();

    root
      .querySelectorAll(selector)
      .forEach(element => {
        const key = normalizeKey(
          element.dataset.key
        );

        const value = element.dataset.value;

        if (key && value) {
          map.set(key, value);
        }
      });

    return map;
  }


  function getGenderMap(root) {
    return makeMap(
      root,
      GENDER_VALUE_SELECTOR
    );
  }


  function getScentMap(root) {
    return makeMap(
      root,
      SCENT_VALUE_SELECTOR
    );
  }


  function findKeyByValue(map, selectedValue) {
    const normalizedSelected =
      normalizeKey(selectedValue);

    for (const [key, value] of map.entries()) {
      if (
        value === selectedValue ||
        normalizeKey(value) === normalizedSelected
      ) {
        return key;
      }
    }

    return normalizedSelected;
  }


  /* ============================================================
     PULSE / LOOP COLLECTION SWITCHER
     ============================================================ */

  function getFormatFromUrl(root) {
    if (!root) {
      return '';
    }

    const url = new URL(
      window.location.href
    );

    const paramName =
      root.dataset.typeParam || '';

    let selectedValues = [];

    if (paramName) {
      selectedValues =
        getFilterValues(
          url,
          paramName
        );
    } else {
      /*
       * Fallback for cases where Shopify refreshed only the native
       * facet markup and the quick-filter root has an older dataset.
       */
      for (
        const [key, value]
        of url.searchParams.entries()
      ) {
        const normalizedKey =
          String(key).toLowerCase();

        if (
          normalizedKey.includes('product_type') ||
          normalizedKey.includes('product-type')
        ) {
          selectedValues.push(
            ...String(value)
              .split(',')
              .map(item => item.trim())
              .filter(Boolean)
          );
        }
      }

      selectedValues =
        unique(selectedValues);
    }

    if (!selectedValues.length) {
      return '';
    }

    const loopValue =
      root.dataset.loopTypeValue || '';

    const pulseValue =
      root.dataset.pulseTypeValue || '';

    const normalizedSelected =
      selectedValues.map(
        normalizeKey
      );

    const hasExactOrNormalized =
      candidate => {
        if (!candidate) {
          return false;
        }

        const normalizedCandidate =
          normalizeKey(candidate);

        return selectedValues.includes(candidate) ||
          normalizedSelected.includes(
            normalizedCandidate
          );
      };

    if (hasExactOrNormalized(pulseValue)) {
      return 'pulse';
    }

    if (hasExactOrNormalized(loopValue)) {
      return 'loop';
    }

    /*
     * Last-resort matching for stores where Product Type values are
     * named things such as "FairScent Pulse" / "FairScent Loop".
     */
    if (
      normalizedSelected.some(value =>
        value === 'pulse' ||
        value.endsWith('-pulse') ||
        value.includes('fairscent-pulse')
      )
    ) {
      return 'pulse';
    }

    if (
      normalizedSelected.some(value =>
        value === 'loop' ||
        value.endsWith('-loop') ||
        value.includes('fairscent-loop')
      )
    ) {
      return 'loop';
    }

    return '';
  }


  function getCurrentFormat(root) {
    const pathname =
      window.location.pathname.toLowerCase();

    /*
     * The two dedicated collections always win. Their collection
     * pathname itself defines the format.
     */
    if (
      pathname.includes(
        '/collections/fairscent-loop'
      )
    ) {
      return 'loop';
    }

    if (
      pathname.includes(
        '/collections/fairscent-pulse'
      )
    ) {
      return 'pulse';
    }

    /*
     * On brand/normal collections the URL filter is the source of
     * truth. This is important when Concept's native Filter & Sort
     * drawer changes Product Type independently of our quick tabs.
     */
    const fromUrl =
      getFormatFromUrl(root);

    if (
      fromUrl === 'pulse' ||
      fromUrl === 'loop'
    ) {
      return fromUrl;
    }

    const fromLiquid = normalizeKey(
      root.dataset.currentFormat
    );

    if (
      fromLiquid === 'pulse' ||
      fromLiquid === 'loop'
    ) {
      return fromLiquid;
    }

    /* Normal/brand collections default to Loop. */
    return 'loop';
  }


  function syncFormat(root) {
    const activeFormat =
      getCurrentFormat(root);

    /*
     * Keep the root dataset synchronized too, so later quick-filter
     * actions do not use stale Liquid state after a native facet change.
     */
    root.dataset.currentFormat =
      activeFormat;

    root
      .querySelectorAll(FORMAT_SELECTOR)
      .forEach(button => {
        const key = normalizeKey(
          button.dataset.fsFormat
        );

        const active =
          key === activeFormat;

        button.classList.toggle(
          'is-active',
          active
        );

        button.setAttribute(
          'aria-selected',
          active ? 'true' : 'false'
        );

        button.disabled = false;
      });
  }


  function applyFormatFilter(
    root,
    formatKey,
    historyMode = 'push'
  ) {
    const paramName =
      root.dataset.typeParam;

    const filterValue =
      getFormatFilterValue(
        root,
        formatKey
      );

    if (!paramName || !filterValue) {
      console.warn(
        '[FairScent Quick Filters] Product Type filter is unavailable for this collection.',
        {
          paramName,
          formatKey,
          filterValue
        }
      );

      return false;
    }

    const url = new URL(
      window.location.href
    );

    clearPaging(url);

    /*
     * Pulse and Loop are mutually exclusive on a normal
     * collection. Change ONLY the Product Type query
     * parameter and keep the current collection pathname.
     */
    removeProductTypeFilters(url);

    url.searchParams.append(
      paramName,
      filterValue
    );

    /*
     * Update the tab immediately. The returned AJAX section
     * will contain the same state once Shopify finishes.
     */
    root.dataset.currentFormat =
      formatKey;

    syncFormat(root);

    /*
     * IMPORTANT: use the exact same AJAX renderer used by
     * Gender / Scent / Brand / Sort. No location.assign(),
     * location.replace(), or form submission is used here.
     */
    renderResults(
      url,
      root,
      {
        historyMode,
        allowHardReload: false
      }
    );

    return true;
  }


  function selectFormat(root, formatKey) {
    if (
      formatKey !== 'pulse' &&
      formatKey !== 'loop'
    ) {
      return;
    }

    closeOpenPanel();

    /*
     * NORMAL COLLECTION
     *
     * Example:
     * /collections/hermes
     *
     * Pulse / Loop behave as Product Type filters and
     * NEVER leave the Hermes collection.
     */
    if (!isDedicatedFormatCollection(root)) {
      const url = new URL(
        window.location.href
      );

      const paramName =
        root.dataset.typeParam;

      const filterValue =
        getFormatFilterValue(
          root,
          formatKey
        );

      const selectedValues =
        getFilterValues(
          url,
          paramName
        );

      if (
        filterValue &&
        selectedValues.length === 1 &&
        selectedValues[0] === filterValue
      ) {
        return;
      }

      applyFormatFilter(
        root,
        formatKey
      );

      return;
    }

    if (
      formatKey === getCurrentFormat(root)
    ) {
      return;
    }

    const targetPath =
      formatKey === 'loop'
        ? (
            root.dataset.loopUrl ||
            '/collections/fairscent-loop'
          )
        : (
            root.dataset.pulseUrl ||
            '/collections/fairscent-pulse'
          );

    const targetUrl = new URL(
      targetPath,
      window.location.origin
    );

    const currentUrl = new URL(
      window.location.href
    );

    currentUrl.searchParams.forEach(
      (value, key) => {
        if (
          key === 'page' ||
          key === 'section_id'
        ) {
          return;
        }

        const normalizedKey =
          String(key).toLowerCase();

        if (
          normalizedKey.includes('product_type') ||
          normalizedKey.includes('product-type')
        ) {
          return;
        }

        targetUrl.searchParams.append(
          key,
          value
        );
      }
    );

    window.location.assign(
      targetUrl.pathname +
      targetUrl.search +
      targetUrl.hash
    );
  }


  /* ============================================================
     DEFAULT FORMAT ON NORMAL COLLECTIONS
     ============================================================ */

  function ensureDefaultFormatFilter(root) {
    if (
      !root ||
      isDedicatedFormatCollection(root)
    ) {
      return false;
    }

    const url = new URL(
      window.location.href
    );

    if (hasActiveFormatFilter(root, url)) {
      return false;
    }

    return applyFormatFilter(
      root,
      'loop',
      'replace'
    );
  }


  /* ============================================================
     NATIVE FACET REMOVE / CLEAR ALL — PRESERVE FORMAT
     ============================================================ */

  function getFormatPreservedFacetUrl(
    root,
    href
  ) {
    if (
      !root ||
      !isBrandCollection(root) ||
      !href
    ) {
      return null;
    }

    let targetUrl;

    try {
      targetUrl = new URL(
        href,
        window.location.origin
      );
    } catch (error) {
      return null;
    }

    /* Only touch links for the current brand collection. */
    const currentPath =
      window.location.pathname
        .replace(/\/+$/, '') || '/';

    const targetPath =
      targetUrl.pathname
        .replace(/\/+$/, '') || '/';

    if (
      targetUrl.origin !== window.location.origin ||
      targetPath !== currentPath
    ) {
      return null;
    }

    const formatKey =
      getCurrentFormat(root);

    const formatValue =
      getFormatFilterValue(
        root,
        formatKey
      );

    if (!formatValue) {
      return null;
    }

    const typeParam =
      root.dataset.typeParam ||
      'filter.p.product_type';

    /*
     * Concept generates Clear All from collection.url, which strips
     * every query parameter. On brand collections Format is mandatory,
     * so every native facet-remove URL must keep the current Loop/Pulse.
     */
    removeProductTypeFilters(
      targetUrl
    );

    targetUrl.searchParams.append(
      typeParam,
      formatValue
    );

    targetUrl.searchParams.delete(
      'section_id'
    );

    return (
      targetUrl.pathname +
      targetUrl.search +
      targetUrl.hash
    );
  }


  function patchNativeFacetRemoveLink(
    root,
    link
  ) {
    if (
      !root ||
      !link ||
      typeof link.getAttribute !== 'function'
    ) {
      return;
    }

    const href =
      link.getAttribute('href');

    const patchedHref =
      getFormatPreservedFacetUrl(
        root,
        href
      );

    if (
      patchedHref &&
      patchedHref !== href
    ) {
      link.setAttribute(
        'href',
        patchedHref
      );
    }
  }


  function patchNativeFacetRemoveLinks(root) {
    if (
      !root ||
      !isBrandCollection(root)
    ) {
      return;
    }

    document
      .querySelectorAll(
        'a[is="facet-remove"][href]'
      )
      .forEach(link => {
        patchNativeFacetRemoveLink(
          root,
          link
        );
      });
  }



  /* ============================================================
     GENDER INTENT — PRESERVE MEN / WOMEN ACROSS COLLECTIONS
     ============================================================ */

  const GENDER_INTENT_PARAM =
    'fs_gender';

  const UNISEX_INTENT_PARAM =
    'fs_unisex';

  const GENDER_MODE_STORAGE_KEY =
    'fs:qf:gender-mode';

  const GENDER_UNISEX_STORAGE_KEY =
    'fs:qf:gender-unisex';

  const GENDER_PARAM_STORAGE_KEY =
    'fs:qf:gender-param';

  const GENDER_VALUES_STORAGE_KEY =
    'fs:qf:gender-values';


  function safeSessionGet(key) {
    try {
      return window.sessionStorage
        ? window.sessionStorage.getItem(key)
        : null;
    } catch (error) {
      return null;
    }
  }


  function safeSessionSet(
    key,
    value
  ) {
    try {
      if (!window.sessionStorage) {
        return;
      }

      if (
        value === null ||
        value === undefined ||
        value === ''
      ) {
        window.sessionStorage.removeItem(
          key
        );

        return;
      }

      window.sessionStorage.setItem(
        key,
        String(value)
      );
    } catch (error) {
      /* Storage is optional; URL state still works. */
    }
  }


  function readCachedGenderValues() {
    const raw =
      safeSessionGet(
        GENDER_VALUES_STORAGE_KEY
      );

    if (!raw) {
      return {};
    }

    try {
      const parsed =
        JSON.parse(raw);

      return (
        parsed &&
        typeof parsed === 'object'
      )
        ? parsed
        : {};
    } catch (error) {
      return {};
    }
  }


  function cacheGenderFilterData(root) {
    if (!root) {
      return;
    }

    const paramName =
      root.dataset.genderParam || '';

    if (paramName) {
      safeSessionSet(
        GENDER_PARAM_STORAGE_KEY,
        paramName
      );
    }

    const currentMap =
      getGenderMap(root);

    const cached =
      readCachedGenderValues();

    let changed = false;

    [
      'male',
      'female',
      'unisex'
    ].forEach(key => {
      const value =
        currentMap.get(key);

      if (
        value &&
        cached[key] !== value
      ) {
        cached[key] = value;
        changed = true;
      }
    });

    if (changed) {
      safeSessionSet(
        GENDER_VALUES_STORAGE_KEY,
        JSON.stringify(cached)
      );
    }
  }


  function getEffectiveGenderParam(root) {
    if (!root) {
      return '';
    }

    return (
      root.dataset.genderParam ||
      safeSessionGet(
        GENDER_PARAM_STORAGE_KEY
      ) ||
      ''
    );
  }


  function getGenderValue(
    root,
    key
  ) {
    if (!root || !key) {
      return '';
    }

    const currentMap =
      getGenderMap(root);

    const currentValue =
      currentMap.get(key);

    if (currentValue) {
      return currentValue;
    }

    const cached =
      readCachedGenderValues();

    return cached[key] || '';
  }


  function getGenderModeFromState(
    root,
    url
  ) {
    if (!root) {
      return 'all';
    }

    cacheGenderFilterData(root);

    const paramName =
      getEffectiveGenderParam(root);

    const selected =
      new Set(
        getFilterValues(
          url,
          paramName
        )
      );

    const male =
      getGenderValue(
        root,
        'male'
      );

    const female =
      getGenderValue(
        root,
        'female'
      );

    const unisex =
      getGenderValue(
        root,
        'unisex'
      );

    const hasMale =
      Boolean(
        male &&
        selected.has(male)
      );

    const hasFemale =
      Boolean(
        female &&
        selected.has(female)
      );

    const hasUnisex =
      Boolean(
        unisex &&
        selected.has(unisex)
      );

    /*
     * Real applied Male/Female values are strongest evidence.
     * This lets the native Filter & Sort drawer override stale
     * semantic state whenever a concrete gender is available.
     */
    if (
      hasMale &&
      !hasFemale
    ) {
      safeSessionSet(
        GENDER_MODE_STORAGE_KEY,
        'men'
      );

      return 'men';
    }

    if (
      hasFemale &&
      !hasMale
    ) {
      safeSessionSet(
        GENDER_MODE_STORAGE_KEY,
        'women'
      );

      return 'women';
    }

    if (
      hasMale &&
      hasFemale
    ) {
      return 'all';
    }

    /*
     * On collections where Male or Female disappears from
     * Shopify's available facet values, preserve what the user
     * actually chose through a small semantic URL state.
     */
    const explicitMode =
      normalizeKey(
        url.searchParams.get(
          GENDER_INTENT_PARAM
        )
      );

    if (
      explicitMode === 'men' ||
      explicitMode === 'women'
    ) {
      safeSessionSet(
        GENDER_MODE_STORAGE_KEY,
        explicitMode
      );

      return explicitMode;
    }

    /*
     * If only Unisex is physically selectable in this collection,
     * session state disambiguates "Men + Unisex" from "Women +
     * Unisex". We do NOT use this fallback when no gender filter is
     * applied, so Clear All still correctly returns to All.
     */
    if (hasUnisex) {
      const storedMode =
        normalizeKey(
          safeSessionGet(
            GENDER_MODE_STORAGE_KEY
          )
        );

      if (
        storedMode === 'men' ||
        storedMode === 'women'
      ) {
        return storedMode;
      }
    }

    return 'all';
  }


  function getIncludeUnisexState(
    root,
    url,
    genderMode
  ) {
    if (
      genderMode !== 'men' &&
      genderMode !== 'women'
    ) {
      return false;
    }

    const explicit =
      String(
        url.searchParams.get(
          UNISEX_INTENT_PARAM
        ) || ''
      ).toLowerCase();

    if (
      explicit === '1' ||
      explicit === 'true' ||
      explicit === 'yes'
    ) {
      safeSessionSet(
        GENDER_UNISEX_STORAGE_KEY,
        '1'
      );

      return true;
    }

    if (
      explicit === '0' ||
      explicit === 'false' ||
      explicit === 'no'
    ) {
      safeSessionSet(
        GENDER_UNISEX_STORAGE_KEY,
        '0'
      );

      return false;
    }

    const paramName =
      getEffectiveGenderParam(root);

    const unisex =
      getGenderValue(
        root,
        'unisex'
      );

    const selected =
      new Set(
        getFilterValues(
          url,
          paramName
        )
      );

    if (
      unisex &&
      selected.has(unisex)
    ) {
      safeSessionSet(
        GENDER_UNISEX_STORAGE_KEY,
        '1'
      );

      return true;
    }

    const stored =
      safeSessionGet(
        GENDER_UNISEX_STORAGE_KEY
      );

    if (stored === '0') {
      return false;
    }

    if (stored === '1') {
      return true;
    }

    /*
     * Men/Women quick filters default to including Unisex.
     */
    return true;
  }


  function persistGenderIntent(
    url,
    genderMode,
    includeUnisex
  ) {
    if (
      genderMode !== 'men' &&
      genderMode !== 'women'
    ) {
      url.searchParams.delete(
        GENDER_INTENT_PARAM
      );

      url.searchParams.delete(
        UNISEX_INTENT_PARAM
      );

      safeSessionSet(
        GENDER_MODE_STORAGE_KEY,
        null
      );

      safeSessionSet(
        GENDER_UNISEX_STORAGE_KEY,
        null
      );

      return;
    }

    url.searchParams.set(
      GENDER_INTENT_PARAM,
      genderMode
    );

    url.searchParams.set(
      UNISEX_INTENT_PARAM,
      includeUnisex
        ? '1'
        : '0'
    );

    safeSessionSet(
      GENDER_MODE_STORAGE_KEY,
      genderMode
    );

    safeSessionSet(
      GENDER_UNISEX_STORAGE_KEY,
      includeUnisex
        ? '1'
        : '0'
    );
  }


  function clearGenderIntentState() {
    safeSessionSet(
      GENDER_MODE_STORAGE_KEY,
      null
    );

    safeSessionSet(
      GENDER_UNISEX_STORAGE_KEY,
      null
    );
  }


  function getDesiredGenderValues(
    root,
    genderMode,
    includeUnisex
  ) {
    const values = [];

    if (genderMode === 'men') {
      const male =
        getGenderValue(
          root,
          'male'
        );

      if (male) {
        values.push(male);
      }
    }

    if (genderMode === 'women') {
      const female =
        getGenderValue(
          root,
          'female'
        );

      if (female) {
        values.push(female);
      }
    }

    if (includeUnisex) {
      const unisex =
        getGenderValue(
          root,
          'unisex'
        );

      if (unisex) {
        values.push(unisex);
      }
    }

    return unique(values);
  }


  function applyGenderIntentToUrl(
    root,
    url,
    genderMode,
    includeUnisex
  ) {
    cacheGenderFilterData(root);

    const paramName =
      getEffectiveGenderParam(root);

    persistGenderIntent(
      url,
      genderMode,
      includeUnisex
    );

    if (!paramName) {
      return false;
    }

    const before =
      getFilterValues(
        url,
        paramName
      ).slice().sort();

    const desired =
      getDesiredGenderValues(
        root,
        genderMode,
        includeUnisex
      );

    url.searchParams.delete(
      paramName
    );

    desired.forEach(value => {
      url.searchParams.append(
        paramName,
        value
      );
    });

    const after =
      getFilterValues(
        url,
        paramName
      ).slice().sort();

    return (
      before.length !== after.length ||
      before.some(
        (value, index) =>
          value !== after[index]
      )
    );
  }


  function ensureGenderIntentFilter(root) {
    if (!root) {
      return false;
    }

    const url =
      new URL(
        window.location.href
      );

    const explicitMode =
      normalizeKey(
        url.searchParams.get(
          GENDER_INTENT_PARAM
        )
      );

    if (
      explicitMode !== 'men' &&
      explicitMode !== 'women'
    ) {
      return false;
    }

    const includeUnisex =
      getIncludeUnisexState(
        root,
        url,
        explicitMode
      );

    const changed =
      applyGenderIntentToUrl(
        root,
        url,
        explicitMode,
        includeUnisex
      );

    if (!changed) {
      return false;
    }

    renderResults(
      url,
      root,
      {
        historyMode: 'replace',
        allowHardReload: false
      }
    );

    return true;
  }


  /* ============================================================
     GENDER SYNC
     ============================================================ */

  function syncGender(root, url) {
    cacheGenderFilterData(root);

    const paramName =
      getEffectiveGenderParam(root);

    const activeGender =
      getGenderModeFromState(
        root,
        url
      );

    root
      .querySelectorAll(GENDER_SELECTOR)
      .forEach(button => {
        const key = normalizeKey(
          button.dataset.fsGender
        );

        const active =
          key === activeGender;

        button.classList.toggle(
          'is-active',
          active
        );

        button.setAttribute(
          'aria-pressed',
          active ? 'true' : 'false'
        );

        /*
         * Do not disable Men merely because the current collection
         * has no Male products. If we know the gender parameter from
         * this or a previous collection, the semantic choice remains
         * valid and can be carried to another brand.
         */
        button.disabled =
          !paramName;
      });
  }


  /* ============================================================
     INCLUDE UNISEX SYNC
     ============================================================ */

  function syncUnisex(root, url) {
    const row =
      root.querySelector(
        UNISEX_ROW_SELECTOR
      );

    const toggle =
      root.querySelector(
        UNISEX_TOGGLE_SELECTOR
      );

    if (!row || !toggle) {
      return;
    }

    cacheGenderFilterData(root);

    const paramName =
      getEffectiveGenderParam(root);

    const activeGender =
      getGenderModeFromState(
        root,
        url
      );

    const shouldShow =
      activeGender === 'men' ||
      activeGender === 'women';

    row.hidden =
      !shouldShow;

    if (!shouldShow) {
      return;
    }

    const includeUnisex =
      getIncludeUnisexState(
        root,
        url,
        activeGender
      );

    toggle.classList.toggle(
      'is-active',
      includeUnisex
    );

    toggle.setAttribute(
      'aria-pressed',
      includeUnisex
        ? 'true'
        : 'false'
    );

    /*
     * Keep this usable even when Unisex has zero products in the
     * current brand. The preference is still preserved for the next
     * brand where Unisex may exist.
     */
    toggle.disabled =
      !paramName;
  }


  /* ============================================================
     SCENT SYNC
     ============================================================ */

  function syncScents(root, url) {
    const filterFound =
      root.dataset.scentFilterFound === 'true';

    const paramName =
      root.dataset.scentParam;

    const map =
      getScentMap(root);

    const selectedValues =
      getFilterValues(
        url,
        paramName
      );

    const selectedKeys =
      new Set();

    selectedValues.forEach(value => {
      const key =
        findKeyByValue(
          map,
          value
        );

      if (
        SCENT_KEYS.includes(key)
      ) {
        selectedKeys.add(key);
      }
    });

    root
      .querySelectorAll(SCENT_SELECTOR)
      .forEach(button => {
        const key =
          normalizeKey(
            button.dataset.fsScent
          );

        const active =
          selectedKeys.has(key);

        button.classList.toggle(
          'is-active',
          active
        );

        button.setAttribute(
          'aria-pressed',
          active ? 'true' : 'false'
        );

        button.disabled =
          !filterFound;
      });
  }


  /* ============================================================
     PANEL HELPERS
     ============================================================ */

  function getBrandPanel(root) {
    if (
      openPanelState &&
      openPanelState.root === root &&
      openPanelState.type === 'brand'
    ) {
      return openPanelState.panel;
    }

    return root.querySelector(
      BRAND_PANEL_SELECTOR
    );
  }


  function getSortPanel(root) {
    if (
      openPanelState &&
      openPanelState.root === root &&
      openPanelState.type === 'sort'
    ) {
      return openPanelState.panel;
    }

    return root.querySelector(
      SORT_PANEL_SELECTOR
    );
  }


  /* ============================================================
     BRAND SYNC
     ============================================================ */

  function syncBrand(root, url) {
    const toggle =
      root.querySelector(
        BRAND_TOGGLE_SELECTOR
      );

    const panel =
      getBrandPanel(root);

    /*
     * BRAND COLLECTION MODE
     * Example: /collections/hermes
     * Brand is a collection switcher, not the local vendor facet.
     */
    if (isBrandCollection(root)) {
      if (toggle) {
        toggle.disabled = false;
        toggle.classList.add(
          'is-active'
        );
      }

      if (!panel) {
        return;
      }

      const currentHandle =
        normalizeKey(
          root.dataset.currentBrandHandle
        );

      panel
        .querySelectorAll(
          BRAND_COLLECTION_OPTION_SELECTOR
        )
        .forEach(option => {
          const handle =
            normalizeKey(
              option.dataset
                .fsBrandCollectionHandle
            );

          const active =
            Boolean(
              handle &&
              handle === currentHandle
            );

          option.classList.toggle(
            'is-active',
            active
          );
        });

      return;
    }

    /*
     * FILTER MODE
     * FairScent Pulse / Loop keep Shopify's vendor filter.
     */
    const filterFound =
      root.dataset.brandFilterFound === 'true';

    const paramName =
      root.dataset.brandParam;

    const selected =
      getFilterValues(
        url,
        paramName
      );

    if (toggle) {
      toggle.disabled =
        !filterFound;

      toggle.classList.toggle(
        'is-active',
        selected.length > 0
      );
    }

    if (!panel) {
      return;
    }

    panel
      .querySelectorAll(
        BRAND_OPTION_SELECTOR
      )
      .forEach(option => {
        const value =
          option.dataset.fsBrandValue || '';

        const active =
          value
            ? selected.includes(value)
            : selected.length === 0;

        option.classList.toggle(
          'is-active',
          active
        );
      });
  }


  /* ============================================================
     SORT SYNC
     ============================================================ */

  function syncSort(root, url) {
    const defaultSort =
      root.dataset.defaultSort || '';

    const activeSort =
      url.searchParams.get('sort_by') ||
      defaultSort;

    const toggle =
      root.querySelector(
        SORT_TOGGLE_SELECTOR
      );

    if (toggle) {
      toggle.classList.toggle(
        'is-active',
        Boolean(
          activeSort &&
          activeSort !== defaultSort
        )
      );
    }

    const panel =
      getSortPanel(root);

    if (!panel) {
      return;
    }

    panel
      .querySelectorAll(
        SORT_OPTION_SELECTOR
      )
      .forEach(option => {
        option.classList.toggle(
          'is-active',
          option.dataset.fsSortValue ===
            activeSort
        );
      });
  }


  /* ============================================================
     FLOATING DROPDOWN PORTAL
     ============================================================ */

  function copyRootVariablesToPanel(
    root,
    panel
  ) {
    const styles =
      window.getComputedStyle(root);

    const variables = [
      '--fs-qf-accent',
      '--fs-qf-text',
      '--fs-qf-border',
      '--fs-qf-border-dark',
      '--fs-qf-active-text'
    ];

    variables.forEach(variable => {
      const value =
        styles.getPropertyValue(variable);

      if (value) {
        panel.style.setProperty(
          variable,
          value.trim()
        );
      }
    });
  }


  function resetPanelPosition(panel) {
    if (!panel) {
      return;
    }

    [
      'position',
      'top',
      'left',
      'right',
      'bottom',
      'width',
      'max-width',
      'z-index',
      'pointer-events',
      'isolation'
    ].forEach(property => {
      panel.style.removeProperty(
        property
      );
    });
  }


  /* ============================================================
     POSITION PANEL
     ============================================================ */

  function positionPanel() {
    if (
      !openPanelState ||
      !openPanelState.panel ||
      !openPanelState.root ||
      openPanelState.panel.hidden
    ) {
      return;
    }

    const {
      root,
      panel
    } = openPanelState;

    const toolbar =
      root.querySelector(
        '.fs-qf__toolbar'
      );

    if (!toolbar) {
      return;
    }

    const rect =
      toolbar.getBoundingClientRect();

    const viewportWidth =
      document.documentElement.clientWidth ||
      window.innerWidth;

    const viewportHeight =
      document.documentElement.clientHeight ||
      window.innerHeight;

    let sideGap = 14;

    if (viewportWidth <= 370) {
      sideGap = 9;
    } else if (
      viewportWidth >= 990
    ) {
      sideGap = 24;
    }

    let panelWidth =
      rect.width - (sideGap * 2);

    panelWidth =
      Math.max(
        180,
        panelWidth
      );

    if (viewportWidth >= 990) {
      panelWidth =
        Math.min(
          420,
          panelWidth
        );
    }

    panelWidth =
      Math.min(
        panelWidth,
        viewportWidth - 16
      );

    let left =
      rect.left + sideGap;

    if (viewportWidth >= 990) {
      left =
        rect.right -
        sideGap -
        panelWidth;
    }

    left =
      Math.max(
        8,
        Math.min(
          left,
          viewportWidth -
            panelWidth -
            8
        )
      );

    let top =
      rect.bottom + 3;

    const naturalHeight =
      Math.min(
        panel.scrollHeight || 340,
        Math.min(
          340,
          viewportHeight * 0.55
        )
      );

    const roomBelow =
      viewportHeight -
      rect.bottom -
      8;

    const roomAbove =
      rect.top - 8;

    if (
      roomBelow <
        Math.min(
          160,
          naturalHeight
        ) &&
      roomAbove > roomBelow
    ) {
      top =
        Math.max(
          8,
          rect.top -
            naturalHeight -
            3
        );
    }

    panel.style.setProperty(
      'position',
      'fixed',
      'important'
    );

    panel.style.setProperty(
      'top',
      `${Math.round(top)}px`,
      'important'
    );

    panel.style.setProperty(
      'left',
      `${Math.round(left)}px`,
      'important'
    );

    panel.style.setProperty(
      'right',
      'auto',
      'important'
    );

    panel.style.setProperty(
      'bottom',
      'auto',
      'important'
    );

    panel.style.setProperty(
      'width',
      `${Math.round(panelWidth)}px`,
      'important'
    );

    panel.style.setProperty(
      'max-width',
      `${Math.round(panelWidth)}px`,
      'important'
    );

    panel.style.setProperty(
      'z-index',
      '2147483647',
      'important'
    );

    panel.style.setProperty(
      'pointer-events',
      'auto',
      'important'
    );

    panel.style.setProperty(
      'isolation',
      'isolate',
      'important'
    );
  }


  /* ============================================================
     CLOSE FLOATING PANEL
     ============================================================ */

  function closeOpenPanel() {
    if (!openPanelState) {
      return;
    }

    const {
      panel,
      toggle,
      placeholder
    } = openPanelState;

    panel.hidden = true;

    if (toggle) {
      toggle.setAttribute(
        'aria-expanded',
        'false'
      );
    }

    resetPanelPosition(panel);

    if (
      placeholder &&
      placeholder.parentNode
    ) {
      placeholder.parentNode.insertBefore(
        panel,
        placeholder
      );

      placeholder.remove();
    } else {
      if (
        panel.parentNode ===
        document.body
      ) {
        panel.remove();
      }
    }

    openPanelState = null;
  }


  function closePanels(root) {
    if (
      openPanelState &&
      (
        !root ||
        openPanelState.root === root
      )
    ) {
      closeOpenPanel();
    }
  }


  function closeAllPanels() {
    closeOpenPanel();

    getRoots().forEach(root => {
      const brandPanel =
        root.querySelector(
          BRAND_PANEL_SELECTOR
        );

      const sortPanel =
        root.querySelector(
          SORT_PANEL_SELECTOR
        );

      const brandToggle =
        root.querySelector(
          BRAND_TOGGLE_SELECTOR
        );

      const sortToggle =
        root.querySelector(
          SORT_TOGGLE_SELECTOR
        );

      if (brandPanel) {
        brandPanel.hidden = true;
      }

      if (sortPanel) {
        sortPanel.hidden = true;
      }

      if (brandToggle) {
        brandToggle.setAttribute(
          'aria-expanded',
          'false'
        );
      }

      if (sortToggle) {
        sortToggle.setAttribute(
          'aria-expanded',
          'false'
        );
      }
    });
  }


  /* ============================================================
     OPEN FLOATING PANEL
     ============================================================ */

  function togglePanel(root, type) {
    if (!root) {
      return;
    }

    if (
      openPanelState &&
      openPanelState.root === root &&
      openPanelState.type === type
    ) {
      closeOpenPanel();
      return;
    }

    closeOpenPanel();

    const panelSelector =
      type === 'brand'
        ? BRAND_PANEL_SELECTOR
        : SORT_PANEL_SELECTOR;

    const toggleSelector =
      type === 'brand'
        ? BRAND_TOGGLE_SELECTOR
        : SORT_TOGGLE_SELECTOR;

    const panel =
      root.querySelector(
        panelSelector
      );

    const toggle =
      root.querySelector(
        toggleSelector
      );

    if (
      !panel ||
      !toggle
    ) {
      return;
    }

    const placeholder =
      document.createComment(
        `fs-${type}-panel-placeholder`
      );

    panel.parentNode.insertBefore(
      placeholder,
      panel
    );

    copyRootVariablesToPanel(
      root,
      panel
    );

    openPanelState = {
      root,
      panel,
      toggle,
      placeholder,
      type
    };

    document.body.appendChild(
      panel
    );

    panel.hidden = false;

    toggle.setAttribute(
      'aria-expanded',
      'true'
    );

    requestAnimationFrame(() => {
      positionPanel();
    });
  }


  /* ============================================================
     SYNC ROOT
     ============================================================ */

  function syncRoot(root) {
    if (!root) {
      return;
    }

    const url =
      new URL(
        window.location.href
      );

    syncFormat(root);

    syncGender(
      root,
      url
    );

    syncUnisex(
      root,
      url
    );

    syncScents(
      root,
      url
    );

    syncBrand(
      root,
      url
    );

    syncSort(
      root,
      url
    );
  }


  function syncAll() {
    getRoots().forEach(root => {
      syncRoot(root);
      patchNativeFacetRemoveLinks(root);
    });

    observeSections();
  }


  function queueSync(delay = 0) {
    window.clearTimeout(
      syncTimer
    );

    syncTimer =
      window.setTimeout(
        syncAll,
        delay
      );
  }


  /* ============================================================
     NATIVE FILTER / URL SYNCHRONIZATION
     ============================================================ */

  function syncAfterNativeFilterChange() {
    /*
     * Concept may update its filter drawer, URL and collection DOM
     * in separate animation/AJAX steps. Run a few lightweight syncs
     * so the quick controls always settle on the final URL state.
     */
    requestAnimationFrame(
      syncAll
    );

    [60, 180, 420].forEach(delay => {
      window.setTimeout(
        syncAll,
        delay
      );
    });
  }


  function installHistorySync() {
    if (
      window.__fsQuickFiltersHistorySyncInstalled
    ) {
      return;
    }

    window.__fsQuickFiltersHistorySyncInstalled =
      true;

    ['pushState', 'replaceState'].forEach(
      methodName => {
        const original =
          window.history[methodName];

        if (typeof original !== 'function') {
          return;
        }

        window.history[methodName] =
          function (...args) {
            const result =
              original.apply(
                this,
                args
              );

            window.dispatchEvent(
              new CustomEvent(
                'fs-quick-filters:urlchange'
              )
            );

            return result;
          };
      }
    );
  }


  installHistorySync();


  window.addEventListener(
    'fs-quick-filters:urlchange',
    () => {
      closeOpenPanel();
      syncAfterNativeFilterChange();
    }
  );


  /*
   * Native Concept/Shopify facet controls live outside the custom
   * quick-filter root. This catches their checkbox/select changes as
   * an additional fallback even if a theme version changes how it
   * updates browser history.
   */
  document.addEventListener(
    'change',
    event => {
      const control =
        event.target;

      if (
        !control ||
        typeof control.matches !== 'function' ||
        control.closest(ROOT_SELECTOR)
      ) {
        return;
      }

      if (
        !control.matches(
          'input, select'
        )
      ) {
        return;
      }

      const name =
        String(
          control.name || ''
        ).toLowerCase();

      if (
        name.includes('filter.') ||
        name.includes('filter[') ||
        name === 'sort_by' ||
        name.includes('sort')
      ) {
        syncAfterNativeFilterChange();
      }
    },
    true
  );


  /* Common theme event names; harmless on versions that do not emit them. */
  [
    'facets:updated',
    'facet:updated',
    'filters:updated',
    'collection:updated'
  ].forEach(eventName => {
    document.addEventListener(
      eventName,
      syncAfterNativeFilterChange
    );
  });


  /* ============================================================
     WATCH CONCEPT SECTION REPLACEMENTS
     ============================================================ */

  function observeSections() {
    getRoots().forEach(root => {
      const section =
        root.closest(
          '[id^="shopify-section-"]'
        );

      if (
        !section ||
        section.dataset
          .fsQuickFiltersObserved ===
          'true'
      ) {
        return;
      }

      section.dataset
        .fsQuickFiltersObserved =
        'true';

      const observer =
        new MutationObserver(() => {
          queueSync(50);
        });

      observer.observe(
        section,
        {
          childList: true,
          subtree: true
        }
      );
    });
  }


  /* ============================================================
     AJAX SECTION FETCH
     ============================================================ */

  async function fetchSection(
    url,
    sectionId,
    signal
  ) {
    const requestUrl =
      new URL(
        url.toString()
      );

    requestUrl.searchParams.delete(
      'section_id'
    );

    requestUrl.searchParams.set(
      'section_id',
      sectionId
    );

    const response =
      await fetch(
        requestUrl.toString(),
        {
          method: 'GET',
          credentials: 'same-origin',
          headers: {
            'X-Requested-With':
              'XMLHttpRequest'
          },
          signal
        }
      );

    if (!response.ok) {
      throw new Error(
        `FairScent collection request failed with status ${response.status}`
      );
    }

    const html =
      await response.text();

    const parsed =
      new DOMParser()
        .parseFromString(
          html,
          'text/html'
        );

    const returnedSection =
      parsed.getElementById(
        `shopify-section-${sectionId}`
      );

    return {
      html:
        returnedSection
          ? returnedSection.innerHTML
          : parsed.body.innerHTML
    };
  }


  /* ============================================================
     SCENT SCROLLER POSITION
     ============================================================ */

  function getScrollerPosition(root) {
    const scroller =
      root.querySelector(
        '[data-fs-scent-scroller]'
      );

    return scroller
      ? scroller.scrollLeft
      : 0;
  }


  function restoreScrollerPosition(
    section,
    position
  ) {
    if (!section) {
      return;
    }

    requestAnimationFrame(() => {
      const scroller =
        section.querySelector(
          '[data-fs-scent-scroller]'
        );

      if (scroller) {
        scroller.scrollLeft =
          position;
      }
    });
  }


  /* ============================================================
     RENDER FILTER RESULTS
     ============================================================ */

  async function renderResults(
    requestedUrl,
    sourceRoot,
    options = {}
  ) {
    if (!sourceRoot) {
      return;
    }

    closeOpenPanel();

    const sectionId =
      sourceRoot.dataset.sectionId;

    const allowHardReload =
      options.allowHardReload !== false;

    if (!sectionId) {
      console.error(
        '[FairScent Quick Filters] Missing section id; AJAX update was cancelled.'
      );

      if (allowHardReload) {
        window.location.assign(
          requestedUrl.toString()
        );
      }

      return;
    }

    const currentSection =
      document.getElementById(
        `shopify-section-${sectionId}`
      );

    if (!currentSection) {
      console.error(
        '[FairScent Quick Filters] Collection section not found; AJAX update was cancelled.'
      );

      if (allowHardReload) {
        window.location.assign(
          requestedUrl.toString()
        );
      }

      return;
    }

    const historyMode =
      options.historyMode || 'push';

    const scrollPosition =
      getScrollerPosition(
        sourceRoot
      );

    if (activeRequest) {
      activeRequest.abort();
    }

    activeRequest =
      new AbortController();

    const requestController =
      activeRequest;

    const url =
      new URL(
        requestedUrl.toString()
      );

    clearPaging(url);

    /*
     * Product Type is the Pulse/Loop selector on normal
     * collections, so preserve it there. Dedicated FairScent
     * format collections do not need an extra Product Type filter.
     */
    if (isDedicatedFormatCollection(sourceRoot)) {
      removeProductTypeFilters(url);
    }

    sourceRoot.classList.add(
      'is-loading'
    );

    sourceRoot.setAttribute(
      'aria-busy',
      'true'
    );

    try {
      const result =
        await fetchSection(
          url,
          sectionId,
          requestController.signal
        );

      if (
        activeRequest !==
        requestController
      ) {
        return;
      }

      currentSection.innerHTML =
        result.html;

      const historyUrl =
        cleanHistoryUrl(url);

      if (
        historyMode === 'replace'
      ) {
        window.history.replaceState(
          {
            fsQuickFilters: true
          },
          '',
          historyUrl
        );
      } else if (
        historyMode === 'none'
      ) {
        const currentUrl =
          window.location.pathname +
          window.location.search +
          window.location.hash;

        if (
          currentUrl !== historyUrl
        ) {
          window.history.replaceState(
            {
              fsQuickFilters: true
            },
            '',
            historyUrl
          );
        }
      } else {
        window.history.pushState(
          {
            fsQuickFilters: true
          },
          '',
          historyUrl
        );
      }

      restoreScrollerPosition(
        currentSection,
        scrollPosition
      );

      syncAll();

      document.dispatchEvent(
        new CustomEvent(
          'collection:reloaded',
          {
            bubbles: true,
            detail: {
              source:
                'fs-quick-filters'
            }
          }
        )
      );
    } catch (error) {
      if (
        error.name === 'AbortError'
      ) {
        return;
      }

      console.error(
        'FairScent quick filter error:',
        error
      );

      /*
       * Type switching on normal collections is AJAX-only.
       * Never turn an AJAX error into a full page reload for
       * that action. Other callers keep the old fallback.
       */
      if (allowHardReload) {
        window.location.assign(
          url.toString()
        );
      } else {
        syncAll();
      }
    } finally {
      if (
        activeRequest ===
        requestController
      ) {
        activeRequest = null;
      }

      if (
        document.contains(
          sourceRoot
        )
      ) {
        sourceRoot.classList.remove(
          'is-loading'
        );

        sourceRoot.removeAttribute(
          'aria-busy'
        );
      }
    }
  }


  /* ============================================================
     GENDER ACTION
     ============================================================ */

  function selectGender(
    root,
    genderKey
  ) {
    cacheGenderFilterData(root);

    const paramName =
      getEffectiveGenderParam(root);

    if (!paramName) {
      return;
    }

    const url =
      new URL(
        window.location.href
      );

    clearPaging(url);

    /*
     * ALL clears both the actual Shopify gender values and our
     * semantic Men/Women state.
     */
    if (genderKey === 'all') {
      url.searchParams.delete(
        paramName
      );

      persistGenderIntent(
        url,
        'all',
        false
      );

      syncGender(
        root,
        url
      );

      syncUnisex(
        root,
        url
      );

      renderResults(
        url,
        root
      );

      return;
    }

    if (
      genderKey !== 'men' &&
      genderKey !== 'women'
    ) {
      return;
    }

    /*
     * Men = Male + Unisex
     * Women = Female + Unisex
     *
     * If Male/Female is unavailable in THIS collection, we still
     * remember the semantic choice. This is what keeps the Men or
     * Women tab active and lets the missing value come back when the
     * user switches to a brand where it exists.
     */
    applyGenderIntentToUrl(
      root,
      url,
      genderKey,
      true
    );

    syncGender(
      root,
      url
    );

    syncUnisex(
      root,
      url
    );

    renderResults(
      url,
      root
    );
  }


  /* ============================================================
     INCLUDE UNISEX ACTION
     ============================================================ */

  function toggleUnisex(root) {
    cacheGenderFilterData(root);

    const url =
      new URL(
        window.location.href
      );

    const genderMode =
      getGenderModeFromState(
        root,
        url
      );

    if (
      genderMode !== 'men' &&
      genderMode !== 'women'
    ) {
      return;
    }

    const paramName =
      getEffectiveGenderParam(root);

    if (!paramName) {
      return;
    }

    clearPaging(url);

    const includeUnisex =
      getIncludeUnisexState(
        root,
        url,
        genderMode
      );

    applyGenderIntentToUrl(
      root,
      url,
      genderMode,
      !includeUnisex
    );

    syncGender(
      root,
      url
    );

    syncUnisex(
      root,
      url
    );

    renderResults(
      url,
      root
    );
  }


  /* ============================================================
     SCENT ACTION
     ============================================================ */

  function toggleScent(
    root,
    scentKey
  ) {
    if (
      !SCENT_KEYS.includes(
        scentKey
      )
    ) {
      return;
    }

    const filterFound =
      root.dataset.scentFilterFound ===
      'true';

    const paramName =
      root.dataset.scentParam;

    if (
      !filterFound ||
      !paramName
    ) {
      return;
    }

    const map =
      getScentMap(root);

    const filterValue =
      map.get(scentKey) ||
      SCENT_LABELS[scentKey];

    const url =
      new URL(
        window.location.href
      );

    clearPaging(url);

    const existingValues =
      getFilterValues(
        url,
        paramName
      );

    const selected =
      new Map();

    existingValues.forEach(
      existingValue => {
        const key =
          findKeyByValue(
            map,
            existingValue
          );

        if (
          SCENT_KEYS.includes(key)
        ) {
          selected.set(
            key,
            existingValue
          );
        }
      }
    );

    if (
      selected.has(scentKey)
    ) {
      selected.delete(scentKey);
    } else {
      selected.set(
        scentKey,
        filterValue
      );
    }

    url.searchParams.delete(
      paramName
    );

    SCENT_KEYS.forEach(key => {
      if (
        selected.has(key)
      ) {
        url.searchParams.append(
          paramName,
          selected.get(key)
        );
      }
    });

    renderResults(
      url,
      root
    );
  }


  /* ============================================================
     BRAND ACTION
     ============================================================ */

  function selectBrandCollection(
    root,
    targetPath
  ) {
    if (!root) {
      return;
    }

    closeOpenPanel();

    const currentUrl =
      new URL(
        window.location.href
      );

    const formatKey =
      getCurrentFormat(root);

    const goingToAllBrands =
      !targetPath;

    const destinationPath =
      goingToAllBrands
        ? (
            formatKey === 'pulse'
              ? (
                  root.dataset.pulseUrl ||
                  '/collections/fairscent-pulse'
                )
              : (
                  root.dataset.loopUrl ||
                  '/collections/fairscent-loop'
                )
          )
        : targetPath;

    const targetUrl =
      new URL(
        destinationPath,
        window.location.origin
      );

    const brandParam =
      root.dataset.brandParam || '';

    const typeParam =
      root.dataset.typeParam || '';

    /* Preserve gender, scent, sort and other useful filters. */
    currentUrl.searchParams.forEach(
      (value, key) => {
        if (
          key === 'page' ||
          key === 'section_id'
        ) {
          return;
        }

        if (
          brandParam &&
          key === brandParam
        ) {
          return;
        }

        const normalizedKey =
          String(key).toLowerCase();

        if (
          normalizedKey.includes('vendor')
        ) {
          return;
        }

        /*
         * All brands goes to the dedicated Pulse / Loop
         * collection, so Product Type is redundant there.
         */
        if (
          goingToAllBrands &&
          (
            normalizedKey.includes('product_type') ||
            normalizedKey.includes('product-type')
          )
        ) {
          return;
        }

        targetUrl.searchParams.append(
          key,
          value
        );
      }
    );

    /*
     * Brand-to-brand navigation must preserve Loop / Pulse.
     * If it is not in the current URL yet, add it explicitly.
     */
    if (!goingToAllBrands) {
      const formatValue =
        getFormatFilterValue(
          root,
          formatKey
        );

      const effectiveTypeParam =
        typeParam ||
        'filter.p.product_type';

      const hasType =
        Array.from(
          targetUrl.searchParams.keys()
        ).some(key => {
          const normalized =
            String(key).toLowerCase();

          return (
            normalized.includes('product_type') ||
            normalized.includes('product-type')
          );
        });

      if (
        !hasType &&
        formatValue
      ) {
        targetUrl.searchParams.append(
          effectiveTypeParam,
          formatValue
        );
      }
    }


    /*
     * Preserve the semantic Gender choice across brand changes.
     *
     * Example:
     * Calvin Klein Pulse may have no Male products, so Shopify can
     * physically apply only Unisex. We still carry fs_gender=men.
     * If the destination brand exposes Male again, initialization
     * restores Male + Unisex automatically.
     */
    const genderMode =
      getGenderModeFromState(
        root,
        currentUrl
      );

    if (
      genderMode === 'men' ||
      genderMode === 'women'
    ) {
      const includeUnisex =
        getIncludeUnisexState(
          root,
          currentUrl,
          genderMode
        );

      applyGenderIntentToUrl(
        root,
        targetUrl,
        genderMode,
        includeUnisex
      );
    } else {
      targetUrl.searchParams.delete(
        GENDER_INTENT_PARAM
      );

      targetUrl.searchParams.delete(
        UNISEX_INTENT_PARAM
      );
    }
    window.location.assign(
      targetUrl.pathname +
      targetUrl.search +
      targetUrl.hash
    );
  }


  function selectBrandFallback(
    root,
    brandValue
  ) {
    if (
      !root ||
      !brandValue
    ) {
      return;
    }

    closeOpenPanel();

    const currentUrl =
      new URL(
        window.location.href
      );

    const fallbackPath =
      root.dataset.allFragrancesUrl ||
      '/collections/all';

    const targetUrl =
      new URL(
        fallbackPath,
        window.location.origin
      );

    const brandParam =
      root.dataset.fallbackBrandParam ||
      root.dataset.brandParam ||
      'filter.p.vendor';

    const typeParam =
      root.dataset.typeParam ||
      'filter.p.product_type';

    const formatKey =
      getCurrentFormat(root);

    /*
     * Keep useful non-brand filters while moving away from a
     * dedicated brand collection.
     */
    currentUrl.searchParams.forEach(
      (value, key) => {
        if (
          key === 'page' ||
          key === 'section_id'
        ) {
          return;
        }

        const normalizedKey =
          String(key).toLowerCase();

        if (
          key === brandParam ||
          normalizedKey.includes('vendor')
        ) {
          return;
        }

        /*
         * Rebuild Product Type below so the current Loop/Pulse
         * format is always explicit on the fallback collection.
         */
        if (
          normalizedKey.includes('product_type') ||
          normalizedKey.includes('product-type')
        ) {
          return;
        }

        targetUrl.searchParams.append(
          key,
          value
        );
      }
    );

    /*
     * Apply the selected vendor on the all-fragrances fallback.
     */
    targetUrl.searchParams.delete(
      brandParam
    );

    targetUrl.searchParams.append(
      brandParam,
      brandValue
    );

    /*
     * Preserve Loop / Pulse when the fallback collection contains
     * both formats.
     */
    const formatValue =
      getFormatFilterValue(
        root,
        formatKey
      );

    removeProductTypeFilters(
      targetUrl
    );

    if (formatValue) {
      targetUrl.searchParams.append(
        typeParam,
        formatValue
      );
    }

    /*
     * Preserve the semantic Men/Women + Include Unisex state.
     */
    const genderMode =
      getGenderModeFromState(
        root,
        currentUrl
      );

    if (
      genderMode === 'men' ||
      genderMode === 'women'
    ) {
      const includeUnisex =
        getIncludeUnisexState(
          root,
          currentUrl,
          genderMode
        );

      applyGenderIntentToUrl(
        root,
        targetUrl,
        genderMode,
        includeUnisex
      );
    } else {
      targetUrl.searchParams.delete(
        GENDER_INTENT_PARAM
      );

      targetUrl.searchParams.delete(
        UNISEX_INTENT_PARAM
      );
    }

    clearPaging(
      targetUrl
    );

    window.location.assign(
      targetUrl.pathname +
      targetUrl.search +
      targetUrl.hash
    );
  }


  function selectBrand(
    root,
    brandValue
  ) {
    const filterFound =
      root.dataset.brandFilterFound ===
      'true';

    const paramName =
      root.dataset.brandParam;

    if (
      !filterFound ||
      !paramName
    ) {
      return;
    }

    const url =
      new URL(
        window.location.href
      );

    clearPaging(url);

    url.searchParams.delete(
      paramName
    );

    if (brandValue) {
      url.searchParams.append(
        paramName,
        brandValue
      );
    }

    renderResults(
      url,
      root
    );
  }


  function selectBrandTarget(
    root,
    brandValue,
    brandCollectionUrl
  ) {
    if (!root) {
      return;
    }

    const value =
      String(
        brandValue || ''
      ).trim();

    const collectionUrl =
      String(
        brandCollectionUrl || ''
      ).trim();

    /*
     * All brands:
     * - dedicated brand collection -> current Loop/Pulse collection
     * - elsewhere -> clear the local vendor filter
     */
    if (!value) {
      if (isBrandCollection(root)) {
        selectBrandCollection(
          root,
          ''
        );
      } else {
        selectBrand(
          root,
          ''
        );
      }

      return;
    }

    /*
     * Preferred behavior:
     * if /collections/<brand> exists, use that collection.
     */
    if (collectionUrl) {
      selectBrandCollection(
        root,
        collectionUrl
      );

      return;
    }

    /*
     * A vendor filter cannot switch from one dedicated brand
     * collection to another missing brand collection. Move to the
     * all-fragrances fallback and apply the selected vendor there.
     */
    if (isBrandCollection(root)) {
      selectBrandFallback(
        root,
        value
      );

      return;
    }

    /*
     * No dedicated collection:
     * remain on the current normal / Pulse / Loop collection and
     * use Shopify's vendor filter.
     */
    selectBrand(
      root,
      value
    );
  }



  /* ============================================================
     SORT ACTION
     ============================================================ */

  function selectSort(
    root,
    sortValue
  ) {
    if (!sortValue) {
      return;
    }

    const url =
      new URL(
        window.location.href
      );

    clearPaging(url);

    url.searchParams.set(
      'sort_by',
      sortValue
    );

    renderResults(
      url,
      root
    );
  }


  /* ============================================================
     NATIVE CLEAR ALL / FACET REMOVE CLICK GUARD
     ============================================================ */

  document.addEventListener(
    'click',
    event => {
      const facetRemove =
        event.target.closest(
          'a[is="facet-remove"][href]'
        );

      if (!facetRemove) {
        return;
      }

      const root =
        document.querySelector(
          ROOT_SELECTOR
        );

      if (
        !root ||
        !isBrandCollection(root)
      ) {
        return;
      }

      /*
       * A true "Clear all" should also clear the remembered
       * Men/Women semantic state. Single-chip facet removals should
       * not erase it.
       */
      const facetRemoveText =
        normalizeKey(
          facetRemove.textContent
        );

      if (
        facetRemoveText === 'clear-all' ||
        facetRemoveText === 'clear-filters'
      ) {
        clearGenderIntentState();
      }

      /*
       * Do not prevent the click. Rewrite href first, then let
       * Concept's own facet-remove AJAX handler process it normally.
       */
      patchNativeFacetRemoveLink(
        root,
        facetRemove
      );
    },
    true
  );


  /* ============================================================
     CLICK HANDLER
     ============================================================ */

  document.addEventListener(
    'click',
    event => {

      /* PULSE / LOOP */

      const formatButton =
        event.target.closest(
          FORMAT_SELECTOR
        );

      if (formatButton) {
        event.preventDefault();

        const root =
          getRoot(formatButton);

        if (!root) {
          return;
        }

        selectFormat(
          root,
          normalizeKey(
            formatButton.dataset.fsFormat
          )
        );

        return;
      }


      /* GENDER */

      const genderButton =
        event.target.closest(
          GENDER_SELECTOR
        );

      if (genderButton) {
        event.preventDefault();

        const root =
          getRoot(genderButton);

        if (
          !root ||
          genderButton.disabled
        ) {
          return;
        }

        selectGender(
          root,
          normalizeKey(
            genderButton.dataset.fsGender
          )
        );

        return;
      }


      /* INCLUDE UNISEX */

      const unisexToggle =
        event.target.closest(
          UNISEX_TOGGLE_SELECTOR
        );

      if (unisexToggle) {
        event.preventDefault();

        const root =
          getRoot(unisexToggle);

        if (
          !root ||
          unisexToggle.disabled
        ) {
          return;
        }

        toggleUnisex(root);

        return;
      }


      /* SCENT */

      const scentButton =
        event.target.closest(
          SCENT_SELECTOR
        );

      if (scentButton) {
        event.preventDefault();

        const root =
          getRoot(scentButton);

        if (
          !root ||
          scentButton.disabled
        ) {
          return;
        }

        toggleScent(
          root,
          normalizeKey(
            scentButton.dataset.fsScent
          )
        );

        return;
      }


      /* BRAND OPTION */

      const brandOption =
        event.target.closest(
          `${BRAND_OPTION_SELECTOR}, ${BRAND_COLLECTION_OPTION_SELECTOR}`
        );

      if (brandOption) {
        event.preventDefault();

        const root =
          getRoot(brandOption);

        if (!root) {
          return;
        }

        selectBrandTarget(
          root,
          brandOption.dataset
            .fsBrandValue || '',
          brandOption.dataset
            .fsBrandCollectionUrl || ''
        );

        return;
      }


      /* SORT OPTION */

      const sortOption =
        event.target.closest(
          SORT_OPTION_SELECTOR
        );

      if (sortOption) {
        event.preventDefault();

        const root =
          getRoot(sortOption);

        if (!root) {
          return;
        }

        selectSort(
          root,
          sortOption.dataset
            .fsSortValue
        );

        return;
      }


      /* BRAND BUTTON */

      const brandToggle =
        event.target.closest(
          BRAND_TOGGLE_SELECTOR
        );

      if (brandToggle) {
        event.preventDefault();

        const root =
          getRoot(brandToggle);

        if (
          root &&
          !brandToggle.disabled
        ) {
          togglePanel(
            root,
            'brand'
          );
        }

        return;
      }


      /* SORT BUTTON */

      const sortToggle =
        event.target.closest(
          SORT_TOGGLE_SELECTOR
        );

      if (sortToggle) {
        event.preventDefault();

        const root =
          getRoot(sortToggle);

        if (root) {
          togglePanel(
            root,
            'sort'
          );
        }

        return;
      }


      /* CLICK OUTSIDE */

      if (
        !event.target.closest(
          BRAND_PANEL_SELECTOR
        ) &&
        !event.target.closest(
          SORT_PANEL_SELECTOR
        )
      ) {
        closeOpenPanel();
      }
    }
  );


  /* ============================================================
     ESCAPE
     ============================================================ */

  document.addEventListener(
    'keydown',
    event => {
      if (
        event.key === 'Escape'
      ) {
        closeOpenPanel();
      }
    }
  );


  /* ============================================================
     COLLECTION RELOAD
     ============================================================ */

  document.addEventListener(
    'collection:reloaded',
    () => {
      closeOpenPanel();

      requestAnimationFrame(() => {
        const root =
          document.querySelector(
            ROOT_SELECTOR
          );

        /*
         * After a brand switch / section replacement, Shopify may
         * expose a gender value that did not exist on the previous
         * brand. Restore the full requested Men/Women set once.
         */
        if (
          root &&
          ensureGenderIntentFilter(
            root
          )
        ) {
          return;
        }

        syncAll();
      });

      window.setTimeout(
        syncAll,
        150
      );
    }
  );


  /* ============================================================
     MANUAL SYNC
     ============================================================ */

  document.addEventListener(
    'fs-quick-filters:sync',
    () => {
      queueSync();
    }
  );


  /* ============================================================
     BACK / FORWARD
     ============================================================ */

  window.addEventListener(
    'popstate',
    () => {
      closeOpenPanel();

      const root =
        document.querySelector(
          ROOT_SELECTOR
        );

      if (!root) {
        return;
      }

      renderResults(
        new URL(
          window.location.href
        ),
        root,
        {
          historyMode: 'none',
          allowHardReload: false
        }
      );
    }
  );


  /* ============================================================
     PAGE RESTORE
     ============================================================ */

  window.addEventListener(
    'pageshow',
    () => {
      closeOpenPanel();
      queueSync();
    }
  );


  /* ============================================================
     SHOPIFY THEME EDITOR
     ============================================================ */

  document.addEventListener(
    'shopify:section:load',
    () => {
      closeOpenPanel();
      queueSync(50);
    }
  );


  /* ============================================================
     RESIZE
     ============================================================ */

  window.addEventListener(
    'resize',
    () => {
      if (openPanelState) {
        positionPanel();
      }
    },
    {
      passive: true
    }
  );


  /* ============================================================
     PAGE SCROLL
     ============================================================ */

  window.addEventListener(
    'scroll',
    () => {
      closeOpenPanel();
    },
    {
      passive: true
    }
  );


  /* ============================================================
     INITIALIZE
     ============================================================ */

  const initialRoot =
    document.querySelector(
      ROOT_SELECTOR
    );

  if (
    initialRoot &&
    ensureDefaultFormatFilter(
      initialRoot
    )
  ) {
    return;
  }

  if (
    initialRoot &&
    ensureGenderIntentFilter(
      initialRoot
    )
  ) {
    return;
  }

  syncAll();

})();